import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import authAxios from '../utils/authAxios';
import { Input, Select, Button, DangerButton } from '../styles/TableStyles';
import TypeaheadSelect from '../components/TypeaheadSelect';
import ChargeSearchResultModal from '../components/ChargeSearchResultModal';
import {
  PageContainer,
  PageHeaderBar,
  PageTitle,
  ButtonRow,
  FormCard,
  FormGrid,
  FormField,
  FieldLabel,
  FormFooter,
  RadioGroup,
  RadioCard,
  RadioMain,
  RadioSub,
  ResultsArea,
  GroupBlock,
  GroupHeader,
  GroupMeta,
  ResultsTable,
  ClickableRow,
  EmptyRow,
} from '../styles/ChargeSearchStyles';

const initialFormState = {
  commodityId: '',
  bagType: '',
  chargeByStyleId: '',
  targetValue: '',
  calculationType: 'Total Cost',
};

const fmtCurrency = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : '';
};

export default function ChargeSearchIndexPage() {
  const [commodities, setCommodities] = useState([]); // [{UniqueID, Description}]
  const [bagTypes, setBagTypes] = useState([]);       // [string]
  const [countSizes, setCountSizes] = useState([]);   // [{id, label}]
  const [form, setForm] = useState({ ...initialFormState });
  const [searchGroups, setSearchGroups] = useState([]); // [{searchId, empty, rows, meta}]
  const [searchIdCounter, setSearchIdCounter] = useState(0);
  const [showForm, setShowForm] = useState(true);
  const [searching, setSearching] = useState(false);
  const [modalData, setModalData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authAxios.get('/api/getCommodities');
        if (!cancelled) setCommodities(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch commodities:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // BagType cascade.
  useEffect(() => {
    const id = parseInt(form.commodityId, 10);
    if (!Number.isInteger(id) || id <= 0) {
      setBagTypes([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authAxios.get('/api/getBagTypes', { params: { commodity: id } });
        if (!cancelled) setBagTypes(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!cancelled) console.error('Failed to fetch bag types:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [form.commodityId]);

  // CountSize cascade.
  useEffect(() => {
    const id = parseInt(form.commodityId, 10);
    if (!Number.isInteger(id) || id <= 0 || !form.bagType) {
      setCountSizes([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authAxios.get('/api/getCountSizes', {
          params: { commodity: id, bagType: form.bagType },
        });
        if (!cancelled) setCountSizes(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        if (!cancelled) console.error('Failed to fetch count sizes:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [form.commodityId, form.bagType]);

  const handleCommodityChange = (uniqueId) => {
    setForm((prev) => ({
      ...prev,
      commodityId: String(uniqueId),
      bagType: '',
      chargeByStyleId: '',
    }));
  };

  const handleBagTypeChange = (e) => {
    setForm((prev) => ({
      ...prev,
      bagType: e.target.value,
      chargeByStyleId: '',
    }));
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const canSearch = useMemo(() => {
    return Boolean(
      form.commodityId &&
      form.bagType &&
      form.chargeByStyleId &&
      form.targetValue !== '' &&
      Number.isFinite(parseFloat(form.targetValue))
    );
  }, [form]);

  const handleSearch = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!canSearch || searching) return;
    setSearching(true);
    try {
      const resp = await authAxios.post('/api/searchBoxDifference', {
        chargeByStyleId: parseInt(form.chargeByStyleId, 10),
        targetValue:     parseFloat(form.targetValue),
        calculationType: form.calculationType,
      });
      const data = resp?.data || {};
      const results = Array.isArray(data.results) ? data.results : [];
      const empty   = Boolean(data.empty) || results.length === 0;
      const newId = searchIdCounter + 1;
      setSearchIdCounter(newId);
      setSearchGroups((prev) => [
        ...prev,
        {
          searchId: newId,
          empty,
          rows: results,
          meta: {
            commodityId: form.commodityId,
            bagType: form.bagType,
            chargeByStyleId: form.chargeByStyleId,
            targetValue: form.targetValue,
            calculationType: form.calculationType,
          },
        },
      ]);
      setShowForm(false);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Search failed';
      toast.error(msg);
      console.error('search failed', err);
    } finally {
      setSearching(false);
    }
  }, [canSearch, searching, form, searchIdCounter]);

  const handleNewSearch = () => {
    setShowForm(true);
  };

  const handleClearEntries = () => {
    setSearchGroups([]);
    setSearchIdCounter(0);
    setForm({ ...initialFormState });
    setShowForm(true);
  };

  const handleRowClick = (group, row) => {
    setModalData({
      ...row,
      targetValue: group.meta.targetValue,
      calculationType: group.meta.calculationType,
    });
  };

  const commodityOptions = useMemo(
    () => commodities.map((c) => ({ value: c.UniqueID, label: c.Description })),
    [commodities],
  );

  // Style label for the group header. Look up the commodity description and
  // the count-size label from cached options when possible; otherwise fall
  // back to the raw values.
  const labelForGroup = (group) => {
    const meta = group.meta || {};
    const commLabel = commodityOptions.find((o) => String(o.value) === String(meta.commodityId))?.label;
    const csLabel   = countSizes.find((o) => String(o.id) === String(meta.chargeByStyleId))?.label;
    const parts = [commLabel || `Commodity ${meta.commodityId}`, meta.bagType, csLabel].filter(Boolean);
    return parts.join(' · ');
  };

  return (
    <PageContainer>
      <PageHeaderBar>
        <PageTitle>Charge Search Index</PageTitle>
        <ButtonRow>
          {!showForm && (
            <Button type="button" onClick={handleNewSearch}>New Search</Button>
          )}
          {searchGroups.length > 0 && (
            <DangerButton type="button" onClick={handleClearEntries}>Clear Entries</DangerButton>
          )}
        </ButtonRow>
      </PageHeaderBar>

      {showForm && (
        <FormCard>
          <form onSubmit={handleSearch}>
            <FormGrid>
              <FormField>
                <FieldLabel>Commodity</FieldLabel>
                <TypeaheadSelect
                  value={form.commodityId}
                  onChange={handleCommodityChange}
                  options={commodityOptions}
                  placeholder="Select Commodity"
                  ariaLabel="Commodity"
                  size="lg"
                />
              </FormField>
              <FormField>
                <FieldLabel>Bag Type</FieldLabel>
                <Select
                  name="bagType"
                  value={form.bagType}
                  onChange={handleBagTypeChange}
                  disabled={!form.commodityId}
                >
                  <option value="">Select Bag Type</option>
                  {bagTypes.map((bt) => <option key={bt} value={bt}>{bt}</option>)}
                </Select>
              </FormField>
              <FormField>
                <FieldLabel>Ti × Hi</FieldLabel>
                <Select
                  name="chargeByStyleId"
                  value={form.chargeByStyleId}
                  onChange={handleFieldChange}
                  disabled={!form.bagType}
                >
                  <option value="">Select Ti × Hi</option>
                  {countSizes.map((cs) => (
                    <option key={cs.id} value={cs.id}>{cs.label}</option>
                  ))}
                </Select>
              </FormField>
              <FormField>
                <FieldLabel>Target Value</FieldLabel>
                <Input
                  type="number"
                  step="0.01"
                  name="targetValue"
                  value={form.targetValue}
                  onChange={handleFieldChange}
                  placeholder="0.00"
                />
              </FormField>
            </FormGrid>

            <FormFooter>
              <RadioGroup>
                <RadioCard $active={form.calculationType === 'Total Cost'}>
                  <input
                    type="radio"
                    name="calculationType"
                    value="Total Cost"
                    checked={form.calculationType === 'Total Cost'}
                    onChange={handleFieldChange}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <RadioMain>Total Cost</RadioMain>
                    <RadioSub>Target is Return to Grower</RadioSub>
                  </span>
                </RadioCard>
                <RadioCard $active={form.calculationType === 'Return to Grower'}>
                  <input
                    type="radio"
                    name="calculationType"
                    value="Return to Grower"
                    checked={form.calculationType === 'Return to Grower'}
                    onChange={handleFieldChange}
                  />
                  <span style={{ display: 'flex', flexDirection: 'column' }}>
                    <RadioMain>Return to Grower</RadioMain>
                    <RadioSub>Target is Total Cost</RadioSub>
                  </span>
                </RadioCard>
              </RadioGroup>

              <Button type="submit" disabled={!canSearch || searching}>
                {searching ? 'Searching…' : 'Search'}
              </Button>
            </FormFooter>
          </form>
        </FormCard>
      )}

      {searchGroups.length > 0 && (
        <ResultsArea>
          {searchGroups.map((group) => (
            <GroupBlock key={group.searchId}>
              <GroupHeader>
                <span>Search #{group.searchId} — {labelForGroup(group)}</span>
                <GroupMeta>
                  {group.meta.calculationType} · Target ${parseFloat(group.meta.targetValue || 0).toFixed(2)}
                </GroupMeta>
              </GroupHeader>
              {group.empty ? (
                <EmptyRow>
                  No packaging methods configured for this style. Add Box Difference records in Charge by Style before searching.
                </EmptyRow>
              ) : (
                <ResultsTable>
                  <thead>
                    <tr>
                      <th>Commodity</th>
                      <th>Bag Type</th>
                      <th>Ti × Hi</th>
                      <th className="numeric">Total Chg.</th>
                      <th>Packaging Method</th>
                      <th className="numeric">Total Cost</th>
                      <th className="numeric">Return to Grower</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.rows.map((r, i) => (
                      <ClickableRow
                        key={`${group.searchId}-${i}`}
                        onClick={() => handleRowClick(group, r)}
                      >
                        <td>{r.Commodity}</td>
                        <td>{r.BagType}</td>
                        <td>{r.TiHi}</td>
                        <td className="numeric">{fmtCurrency(r.TotalChgAdjustment)}</td>
                        <td>{r.MethodName}</td>
                        <td className="numeric">{fmtCurrency(r.TotalCost)}</td>
                        <td className="numeric">{fmtCurrency(r.ReturnToGrower)}</td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </ResultsTable>
              )}
            </GroupBlock>
          ))}
        </ResultsArea>
      )}

      {modalData && (
        <ChargeSearchResultModal data={modalData} onClose={() => setModalData(null)} />
      )}
    </PageContainer>
  );
}
